/**
 * btk-loader.js - site-wide animated loader controller
 * ----------------------------------------------------------------
 * Exposes window.BTKLoader. A single full-screen overlay (built lazily,
 * reused) showing a 3/4-view cabinet that assembles + spins, with
 * cycling encouraging copy. Styling lives in btk-loader.css.
 *
 *   BTKLoader.show();                       // default rotating copy
 *   BTKLoader.show({ message: 'Saving…' }); // one fixed line
 *   BTKLoader.show({ messages: ['A','B'] });// custom rotation
 *   BTKLoader.setMessage('Almost there…');  // update copy live
 *   BTKLoader.hide();
 *
 * Safety: auto-hides after ~25s so a missed hide() can't trap the user.
 */
window.BTKLoader = (function () {
  const DEFAULT_MESSAGES = [
    'Assembling your kitchen…',
    'Fitting the carcasses…',
    'Lining up the doors…',
    'Tightening the hinges…',
    'Measuring twice…',
    'Bringing it all together…',
    'Almost ready…'
  ];
  const CYCLE_MS  = 2000;
  const SAFETY_MS = 25000;

  let overlay   = null;
  let cabinet   = null;
  let wordsEl   = null;
  let cycleTimer = null;
  let safetyTimer = null;
  let messages  = DEFAULT_MESSAGES;
  let msgIndex  = 0;
  let cycleMs   = CYCLE_MS;   // per-show rotation speed (opts.intervalMs)
  let holdLast  = false;      // stop on the final message instead of looping (opts.holdLast)
  let minVisibleMs = 0;       // keep the loader up at least this long (opts.minVisibleMs)
  let shownAt   = 0;          // timestamp of the last show()
  let hideTimer = null;       // deferred hide when hidden before minVisibleMs

  function build() {
    if (overlay) {
      // doHide() adopts a server-rendered overlay when it is called before any
      // show() - which is exactly what the planner does, hiding its inline boot
      // loader once the catalogue is up. That path sets `overlay` but not the
      // parts inside it, and this early return then kept them null for the rest
      // of the page's life: every later show() cycled its messages into a
      // wordsEl that did not exist, so the overlay went on displaying whatever
      // text the server had baked in ("Loading your kitchen catalogue…").
      if (!cabinet) cabinet = overlay.querySelector('.btk-loader-cabinet');
      if (!wordsEl) wordsEl = overlay.querySelector('.btk-loader-words');
      return overlay;
    }
    // ADOPT a server-rendered overlay if the page shipped one. The planner
    // inlines this exact markup at the top of its document so a loader is on
    // screen the moment the page starts painting - long before this deferred
    // script has even run. Adopting it (rather than building a second one)
    // means hide(), setMessage() and the message cycle all drive the overlay
    // the user is already looking at.
    const existing = document.querySelector('.btk-loader-overlay');
    if (existing) {
      overlay = existing;
      cabinet = overlay.querySelector('.btk-loader-cabinet');
      wordsEl = overlay.querySelector('.btk-loader-words');
      return overlay;
    }
    overlay = document.createElement('div');
    overlay.className = 'btk-loader-overlay';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    const faces =
      '<div class="btk-loader-face btk-loader-face--back"><div class="btk-loader-panel"></div></div>' +
      '<div class="btk-loader-face btk-loader-face--bottom"><div class="btk-loader-panel"></div></div>' +
      '<div class="btk-loader-face btk-loader-face--left"><div class="btk-loader-panel"></div></div>' +
      '<div class="btk-loader-face btk-loader-face--right"><div class="btk-loader-panel"></div></div>' +
      '<div class="btk-loader-face btk-loader-face--top"><div class="btk-loader-panel"></div></div>' +
      '<div class="btk-loader-face btk-loader-face--front"><div class="btk-loader-panel"></div></div>';
    overlay.innerHTML =
      '<div class="btk-loader-stage">' +
        '<div class="btk-loader-shadow"></div>' +
        '<div class="btk-loader-cabinet">' +
          '<div class="btk-loader-box btk-loader-body">'   + faces + '</div>' +
          '<div class="btk-loader-box btk-loader-plinth">' + faces + '</div>' +
        '</div>' +
      '</div>' +
      '<div class="btk-loader-words"></div>' +
      '<div class="btk-loader-dots"><span></span><span></span><span></span></div>';
    (document.body || document.documentElement).appendChild(overlay);
    cabinet = overlay.querySelector('.btk-loader-cabinet');
    wordsEl = overlay.querySelector('.btk-loader-words');
    return overlay;
  }

  function restartAssembly() {
    if (!cabinet) return;
    cabinet.classList.remove('is-assembling');
    // Force reflow so the animation re-runs from the start on reuse.
    void cabinet.offsetWidth;
    cabinet.classList.add('is-assembling');
  }

  function setMessage(text) {
    if (!wordsEl) return;
    wordsEl.classList.add('is-fading');
    setTimeout(() => {
      wordsEl.textContent = text || '';
      wordsEl.classList.remove('is-fading');
    }, 220);
  }

  function startCycle() {
    stopCycle();
    if (!messages.length) return;
    // First word shows immediately (no fade-out from blank).
    msgIndex = 0;
    if (wordsEl) wordsEl.textContent = messages[0];
    if (messages.length < 2) return;
    cycleTimer = setInterval(() => {
      // holdLast: advance without wrapping and stop once the final message is
      // shown, so the loader settles on the last line. Otherwise loop forever.
      if (holdLast && msgIndex >= messages.length - 1) { stopCycle(); return; }
      msgIndex = holdLast ? msgIndex + 1 : (msgIndex + 1) % messages.length;
      setMessage(messages[msgIndex]);
      if (holdLast && msgIndex >= messages.length - 1) stopCycle();
    }, cycleMs);
  }
  function stopCycle() {
    if (cycleTimer) { clearInterval(cycleTimer); cycleTimer = null; }
  }

  function show(opts) {
    opts = opts || {};
    build();
    if (Array.isArray(opts.messages) && opts.messages.length) {
      messages = opts.messages;
    } else if (typeof opts.message === 'string') {
      messages = [opts.message];
    } else {
      messages = DEFAULT_MESSAGES;
    }
    cycleMs      = (typeof opts.intervalMs === 'number' && opts.intervalMs > 0) ? opts.intervalMs : CYCLE_MS;
    holdLast     = !!opts.holdLast;
    minVisibleMs = (typeof opts.minVisibleMs === 'number' && opts.minVisibleMs > 0) ? opts.minVisibleMs : 0;
    shownAt      = Date.now();
    // Opaque variant: the default overlay is translucent, so it takes on the
    // colour of whatever is behind it. Across a navigation that means the
    // before and after halves would not match. Both halves of the planner
    // hand-off use the solid variant so the swap is invisible.
    overlay.classList.toggle('btk-loader-overlay--opaque', !!opts.opaque);
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    restartAssembly();
    startCycle();
    // rAF so the .is-visible transition actually animates from hidden.
    requestAnimationFrame(() => overlay.classList.add('is-visible'));
    if (safetyTimer) clearTimeout(safetyTimer);
    safetyTimer = setTimeout(doHide, SAFETY_MS);
    return api;
  }

  // Actually tear the loader down.
  function doHide() {
    // A page can ship the overlay in its HTML (the planner does) and call
    // hide() before anything ever called show() - so `overlay` may still be
    // null here even though one is on screen. Adopt it, or the loader would
    // never come down.
    if (!overlay) {
      const existing = document.querySelector('.btk-loader-overlay');
      if (existing) overlay = existing;
    }
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; }
    stopCycle();
    if (overlay) {
      // --inline pins opacity/visibility on so the server-rendered copy is up
      // from the first paint with nothing to transition from. It would also
      // outrank the loss of .is-visible, so it has to come off here or the
      // loader could never be dismissed.
      overlay.classList.remove('btk-loader-overlay--inline');
      overlay.classList.remove('is-visible');
    }
  }

  // Public hide: if the caller asked for a minimum visible time and the work
  // finished sooner, defer the teardown so the message sequence can play out.
  function hide() {
    const elapsed = Date.now() - shownAt;
    if (minVisibleMs && elapsed < minVisibleMs) {
      if (!hideTimer) hideTimer = setTimeout(doHide, minVisibleMs - elapsed);
      return api;
    }
    doHide();
    return api;
  }

  // ─── Planner navigation hand-off ───────────────────────────────────────────
  // Opening the planner has TWO dead periods, in two different documents:
  //
  //   click ──────────────► page starts painting ──────────────► usable
  //         ~1.6s server render          ~1.7s parse + boot
  //         (still on the OLD page)      (planner document)
  //
  // Nothing inside the planner can cover the first gap - that page does not
  // exist yet - so we show the loader HERE, on the page being left. The planner
  // document then ships its own copy of this markup inline, which is already on
  // screen when the old page is torn down. Two overlays, one apparent loader.
  //
  // A navigation destroys all JS state, so there is no continuity to carry:
  // the planner's inline copy simply hard-codes the NEXT line. The message
  // change IS the seam, so the hand-off reads as progress rather than a
  // restart - keep these two in step with the copy in
  // sections/btk-kitchen-planner.liquid.
  const NAV_MESSAGES = [
    'Opening your kitchen planner…'   // planner document continues: "Loading your kitchen catalogue…"
  ];
  // Leaving is the same problem mirrored: the destination renders server-side
  // (the homepage measured 2.32s on 2026-08-17) while the browser carries on
  // showing the planner, so the click appears to do nothing. Only ONE half is
  // needed here - the destination paints promptly once it arrives, so it needs
  // no inline copy of its own.
  const EXIT_MESSAGES = ['Closing planner…'];

  const PLANNER_PATH = /\/pages\/kitchen-planner\/?$/;

  function resolve(href) {
    try { return new URL(href, window.location.href); } catch (e) { return null; }
  }
  function isPlannerHref(href) {
    const u = resolve(href);
    return !!u && u.origin === window.location.origin && PLANNER_PATH.test(u.pathname);
  }
  function goesSomewhereElse(href) {
    const u = resolve(href);
    if (!u) return false;
    if (u.origin !== window.location.origin) return true;   // off-site still navigates
    return (u.pathname + u.search) !== (window.location.pathname + window.location.search);
  }

  document.addEventListener('click', (e) => {
    // Let the browser handle anything that is not a plain left-click
    // navigation - new tab, download, modified click, prevented default.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey
        || e.shiftKey || e.altKey) return;
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
    const href = a.getAttribute('href');
    if (!href || href.charAt(0) === '#' || /^(javascript|mailto|tel):/i.test(href)) return;

    const onPlanner = PLANNER_PATH.test(window.location.pathname);
    const toPlanner = isPlannerHref(href);
    let opts = null;
    if (!onPlanner && toPlanner) {
      opts = { messages: NAV_MESSAGES, holdLast: true, opaque: true };
    } else if (onPlanner && !toPlanner && goesSomewhereElse(href)) {
      opts = { messages: EXIT_MESSAGES, holdLast: true, opaque: true };
    }
    if (!opts) return;

    // Defer by a tick before veiling the page. The planner intercepts plenty of
    // its own links and calls preventDefault from ITS click handler - which may
    // be registered after this one, so e.defaultPrevented is still false right
    // now. A setTimeout(0) runs after every synchronous handler has had its
    // say, so by then defaultPrevented is truthful and we only cover the screen
    // for a click that genuinely navigates. 0ms is imperceptible to the user.
    setTimeout(() => {
      if (e.defaultPrevented) return;
      show(opts);
    }, 0);
  });

  // Back-navigation can restore this page from the bfcache with the overlay
  // still showing - the classic bug with this pattern. Clear it then - and ONLY
  // then (e.persisted). pageshow also fires on every ordinary load, and that
  // took the planner's inline boot loader down part-way through starting up,
  // so the website's Start Planning showed the planner's landing page for a
  // few seconds before its name popup (Arden, 2026-10-07). The planner drops
  // its own loader when it is ready.
  window.addEventListener('pageshow', (e) => { if (e.persisted) doHide(); });
  // A page that ships the loader in its HTML (the planner) and then never gets
  // as far as hiding it - a script error mid-start-up - must not be veiled for
  // good: the same 25s safety show() arms.
  window.addEventListener('load', () => {
    if (!safetyTimer && document.querySelector('.btk-loader-overlay--inline')) safetyTimer = setTimeout(doHide, SAFETY_MS);
  });

  const api = { show, hide, setMessage };
  return api;
})();
